// @flow
import React, { Fragment, useContext } from 'react';
import classnames from 'classnames';
import { isEmpty, noop } from 'lodash';

import { ICONS } from 'Components/utils/icon/icons';
import ResponsiveContext from 'Components/utils/responsive/responsiveContext';
import { t } from 'Src/i18n';
import { TrackClick } from 'Tracking/track-click/TrackClick.jsx';

import { ActiveIndexes } from 'Components/frameworks/active-indexes/ActiveIndexes.jsx';
import { ArrowButton } from 'Components/frameworks/arrow-button/ArrowButton.jsx';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer.jsx';
import { Notification } from 'Components/frameworks/notification/Notification.jsx';
import { Icon } from 'Components/frameworks/icon/Icon.jsx';
import { PostAnswer, type PostAnswerProps } from 'Components/frameworks/post-answer/PostAnswer.jsx';
import {
  PostQuestion,
  type PostQuestionProps,
} from 'Components/frameworks/post-question/PostQuestion.jsx';
import { SliderHandler } from 'Components/frameworks/slider-handler/SliderHandler.jsx';

type Question = {
  ...$Exact<PostQuestionProps>,
  answers: Array<PostAnswerProps>,
  trackingData?: TrackingData,
};

type WibilongLargeProps = {
  noAnswerstext?: string,
  questions: Array<Question>,
  scrollTo?: () => void,
  seeMoreLabel?: string,
};

const clearAndScroll = ({ clear, scrollTo }) => () => {
  clear();
  scrollTo();
};

export function WibilongLarge(props: WibilongLargeProps) {
  const { noAnswerstext, questions = [], scrollTo = noop, seeMoreLabel } = props;

  const { isDesktop } = useContext(ResponsiveContext);

  if (isEmpty(questions)) return null;

  return (
    <SiteContainer>
      <ActiveIndexes hasNoIndexEnabled>
        {({ activeIndexes, clear, update }) => {
          const answers =
            questions[activeIndexes[0]] && questions[activeIndexes[0]].answers
              ? questions[activeIndexes[0]].answers
              : [];

          const onClear = clearAndScroll({ clear, scrollTo });

          const items = questions.map((q, i) => {
            const isActive = activeIndexes.includes(i);
            const hasSomeSelected = !!activeIndexes.length;

            return (
              <div className="p-2" key={q.id}>
                <TrackClick
                  as="div"
                  className={classnames(
                    'bg-white cursor-pointer flex overflow-hidden p-5 rounded shadow-md transition-all',
                    {
                      'opacity-50': hasSomeSelected && !isActive,
                      'hover:scale-1025': !hasSomeSelected,
                      'scale-1025': isActive,
                    },
                  )}
                  data-testid="Wibilong-question"
                  onClick={() => {
                    update(i);
                    scrollTo();
                  }}
                  role="button"
                  style={{ minHeight: 250 }}
                  tabIndex={0}
                  trackingData={!hasSomeSelected ? q.trackingData : null}
                >
                  <PostQuestion
                    {...q}
                    className="w-full"
                    textLimit={isActive ? -1 : 100}
                    titleLimit={isActive ? -1 : 60}
                    seeMoreLabel={!isEmpty(q.answers) ? seeMoreLabel : ''}
                  />
                </TrackClick>
              </div>
            );
          });

          return (
            <Fragment>
              <SliderHandler
                className="flex-1"
                items={items}
                onNext={onClear}
                onPrevious={onClear}
                hasKeyboardSupport
                hasLazyload
                hasSwipeSupport
                nbItemsToScroll={1}
                nbItemsToShow={isDesktop ? 3 : 2}
              >
                {({
                  hasControls,
                  isNextControlDisabled,
                  isPreviousControlDisabled,
                  itemsNode,
                  onNext,
                  onPrevious,
                }) => (
                  <div className="flex">
                    {hasControls && (
                      <div
                        className={classnames('flex-none rtl:mr-3', {
                          invisible: isPreviousControlDisabled,
                        })}
                        style={{ marginTop: '118px' }}
                      >
                        <ArrowButton
                          className="shadow-sm"
                          isDisabled={isPreviousControlDisabled}
                          label={t('common.previous')}
                          onClick={onPrevious}
                        />
                      </div>
                    )}
                    {itemsNode}
                    {hasControls && (
                      <div
                        className={classnames('flex-none rtl:ml-3', {
                          invisible: isNextControlDisabled,
                        })}
                        style={{ marginTop: '118px' }}
                      >
                        <ArrowButton
                          className="shadow-sm"
                          isDisabled={isNextControlDisabled}
                          label={t('common.next')}
                          onClick={onNext}
                          rotation={180}
                        />
                      </div>
                    )}
                  </div>
                )}
              </SliderHandler>

              {!isEmpty(activeIndexes) && (
                <div className="fadeIn flex flex-col items-center mt-10 relative">
                  {!isEmpty(answers) && (
                    <button
                      data-testid="Wibilong-clear"
                      className="reset-button absolute bg-white leading-none mt-3 p-3 rtl:right-0 rtl:mr-10 rounded-full shadow-md text-gray-medium"
                      onClick={onClear}
                      type="button"
                    >
                      <Icon name={ICONS.CROSS} width="1.25rem" />
                    </button>
                  )}
                  <div className="border-t-1 max-w-site-small w-full">
                    {!isEmpty(answers) ? (
                      answers.map(answer => (
                        <PostAnswer {...answer} className="py-8 border-b-1" key={answer.id} />
                      ))
                    ) : (
                      <Notification
                        className="mt-8"
                        hasBorder
                        hasClose
                        onClose={onClear}
                        text={noAnswerstext}
                      />
                    )}
                  </div>
                </div>
              )}
            </Fragment>
          );
        }}
      </ActiveIndexes>
    </SiteContainer>
  );
}
